import React, { FC, useEffect, useState } from "react";
import { useQuery } from "@apollo/client";
import { useDispatch, useSelector } from "react-redux";
import { setSuggestedTrails } from "Containers/Trails";
import { areEqual } from "Utils/areEqual";
import { setStops } from "Containers/Stops";
import ADACampusMap from "Components/ADACampusMap";
import { Loading } from "Components/Loading";
import Modal from "HOC/Modal";
import CIF from "Components/CIF";
import {
  combineAndDrawStopsForInteractiveMap,
  combineStopsForStandardMap,
  isInteractiveMap,
  useShowCif,
} from "Containers/CampusMap/CampusMap";
import { useCheckIsCif } from "Containers/CampusMap/useCheckIsCif";
import { setModules } from "../CampusMap/actions";
import { IRootState } from "../../../store/rootState";
import locationGQL from "../CampusMap/gqlQuery";

const ADACampusMapContainer: FC = () => {
  const { locId, instId, platform: platformShortName, tourid } = useSelector(
    (state: IRootState) => state.tour
  );

  const {
    registration,
    analytics: { userkey, platform, sessionId, originalSource, token },
  } = useSelector((state: IRootState) => state.analytics, areEqual);

  const { suggestedTrails } = useSelector((selectors: any) => selectors.trails);
  const stops = useSelector((selectors: any) => selectors.stops);

  const [instName, setInstName] = useState("");

  const [actionButtonsConfig, setActionButtonsConfig] = useState({
    register: {},
    scheduleVisit: {},
    applyOnline: {},
    learnMore: {},
    showRegistration: true,
  });
  const [defaultActionbuttonsConfig, setDefaultActionButtonsConfig] = useState({
    register: {},
    scheduleVisit: {},
    applyOnline: {},
    learnMore: {},
  });

  const dispatch = useDispatch();
  // GraphQL Queries //
  const { data /* loading, error */ } = useQuery(locationGQL, {
    variables: {
      inst_id: instId,
      loc_id: locId,
    },
    fetchPolicy: "cache-and-network",
    onCompleted: (response) => {
      // Saves trails so data is ready when trails is active //
      dispatch(
        setSuggestedTrails(response?.locations?.interactivemaps[0].customTrails)
      );
      // Get modules from data //
      const allModules = response?.locations?.modules;
      // Store modules //
      dispatch(setModules(allModules));
    },
  });

  // determine if form is legacy or new CIF //
  const isCif = useCheckIsCif(
    data?.locations?.modules ?? [],
    platformShortName,
    originalSource
  );

  const { showCif, onRegisterClick, onCifClose, onCifComplete } = useShowCif(
    dispatch,
    isCif
  );

  useEffect(() => {
    // Action Button Logic
    if (data) {
      const {
        actionButtons: {
          register: {
            name: registerName,
            bgColor: registerColor,
            fontColor: registerFontColor,
          },
          actions: [learnMore, scheduleVisit, applyOnline],
          defaultActions,
        },
      } = data?.locations;
      // eslint-disable-next-line camelcase
      setInstName(data?.locations.inst_name);

      setActionButtonsConfig({
        register: {
          click: onRegisterClick,
          name: registerName,
          bgColor: registerColor,
          fontColor: registerFontColor,
        },
        scheduleVisit,
        applyOnline,
        learnMore,
        showRegistration: !registration.hasRegisteredUser,
      });

      const defaultRegister = defaultActions.find(
        (action) => action.actionid === "register"
      );
      const defaultLearnMore = defaultActions.find(
        (action) => action.actionid === "action1"
      );
      const defaultScheduleVisit = defaultActions.find(
        (action) => action.actionid === "action2"
      );
      const defaultApplyOnline = defaultActions.find(
        (action) => action.actionid === "action3"
      );
      setDefaultActionButtonsConfig({
        register: defaultRegister,
        scheduleVisit: defaultScheduleVisit,
        applyOnline: defaultApplyOnline,
        learnMore: defaultLearnMore,
      });
    }
  }, [data, registration.hasRegisteredUser, onRegisterClick]);

  useEffect(() => {
    if (data?.locations?.interactivemaps?.[0]?.stops) {
      const modules = data?.locations?.modules ?? [];

      let combinedStops;
      if (isInteractiveMap(modules, originalSource, platform)) {
        combinedStops = combineAndDrawStopsForInteractiveMap(
          data?.locations,
          null,
          null,
          tourid
        );
      } else {
        combinedStops = combineStopsForStandardMap(data?.locations, tourid);
      }

      dispatch(setStops(combinedStops));
    }
  }, [data, dispatch, platform, tourid, originalSource]);

  const typeid = actionButtonsConfig.applyOnline?.["typeid"];

  return (
    <>
      {!data && (
        <div
          style={{
            zIndex: 9999999,
            backgroundColor: "#fff",
            position: "fixed",
            height: "100%",
            width: "100%",
            display: "flex",
            alignItems: "center",
          }}
        >
          <Loading />
        </div>
      )}
      <ADACampusMap
        actionButtonsConfig={actionButtonsConfig}
        defaultActionButtonsConfig={defaultActionbuttonsConfig}
        suggestedTrails={suggestedTrails}
        instName={instName}
        originalIMapUrl={`${process.env.REACT_APP_API_BASE_URL}tour/map/${instId}/${locId}`}
        combinedStops={stops}
      />
      {showCif && (
        <Modal onClose={onCifClose}>
          <CIF
            isCif={isCif}
            locId={locId}
            instId={instId}
            tourId={tourid}
            userKey={userkey}
            sessionId={sessionId}
            onFormCompleted={onCifComplete}
            typeId={typeid}
            token={token}
          />
        </Modal>
      )}
    </>
  );
};

export default ADACampusMapContainer;
try {
    // @ts-ignore
    ADACampusMap.displayName = "ADACampusMap";
    // @ts-ignore
    ADACampusMap.__docgenInfo = { "description": "", "displayName": "ADACampusMap", "props": {} };
    // @ts-ignore
    if (typeof STORYBOOK_REACT_CLASSES !== "undefined")
        // @ts-ignore
        STORYBOOK_REACT_CLASSES["src/shared/containers/ADACampusMap/ADACampusMap.tsx#ADACampusMap"] = { docgenInfo: ADACampusMap.__docgenInfo, name: "ADACampusMap", path: "src/shared/containers/ADACampusMap/ADACampusMap.tsx#ADACampusMap" };
}
catch (__react_docgen_typescript_loader_error) { }